import type { ReactNode } from 'react';
import { ETICHETTA_FAMIGLIA_ATTRIBUTO } from '@calcio/shared';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { trattoValore } from './colori-stato.js';
import { FAMIGLIE_MEDIA, type MedieAttributi } from './elenchi.js';

/**
 * Le medie di famiglia in anelli, le sole che hanno un valore (l'overall sta
 * già nella testata): l'arco è lungo quanto il valore su 100 e ha il colore
 * della sua fascia, il numero in grassetto al centro. Sotto, icona e nome; da
 * telefono la sola icona.
 */
export function IndicatoriMedie({ medie }: { medie: MedieAttributi }) {
  const famiglie = FAMIGLIE_MEDIA.flatMap((f) => {
    const valore = medie[f];
    return valore != null ? [{ famiglia: f, valore }] : [];
  });
  if (famiglie.length === 0) return null;
  return (
    <Card>
      <CardContent className="flex gap-2 @md:gap-4">
        {famiglie.map(({ famiglia: f, valore }) => (
          <Indicatore
            key={f}
            valore={valore}
            nome={ETICHETTA_FAMIGLIA_ATTRIBUTO[f]}
            icona={<IconaFamiglia famiglia={f} aria-hidden className="size-4 shrink-0" />}
            titolo={`Media attributi ${ETICHETTA_FAMIGLIA_ATTRIBUTO[f].toLowerCase()}`}
          />
        ))}
      </CardContent>
    </Card>
  );
}

const RAGGIO = 42;
const CIRCONFERENZA = 2 * Math.PI * RAGGIO;

function Indicatore({
  valore,
  nome,
  icona,
  titolo,
}: {
  valore: number;
  nome: string;
  icona: ReactNode;
  titolo: string;
}) {
  const quota = Math.min(Math.max(valore, 0), 100) / 100;
  return (
    <figure className="flex min-w-0 flex-1 flex-col items-center gap-2" title={titolo}>
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-label={`${nome}: ${valore}`}
        className="w-full max-w-24"
      >
        <circle cx="50" cy="50" r={RAGGIO} fill="none" strokeWidth="8" className="stroke-muted" />
        {/* Parte in alto e gira in senso orario. */}
        <circle
          cx="50"
          cy="50"
          r={RAGGIO}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={CIRCONFERENZA}
          strokeDashoffset={CIRCONFERENZA * (1 - quota)}
          transform="rotate(-90 50 50)"
          className={cn('transition-[stroke-dashoffset] duration-700', trattoValore(valore))}
        />
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="30"
          className="fill-foreground font-bold tabular-nums"
        >
          {valore}
        </text>
      </svg>
      <figcaption className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        {icona}
        <span className="hidden truncate @md:inline">{nome}</span>
      </figcaption>
    </figure>
  );
}
